import { fmtNum, fmtPct } from "@/lib/verizonSalesApi";
import { StoreLeaderboard } from "./StoreLeaderboard";
import { StoreInfoCells, StoreInfoHead } from "./StoreInfoCells";
import { TotalsRow } from "./ReportTotals";

const monthName = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
const addr = (s) => [s.address, s.city].filter(Boolean).join(", ");
const storeCols = (admin) => [...(admin ? [{ label: "Dealer", get: (s) => s.sub_agent_name }, { label: "Address", get: addr }] : []),
  { label: "Store ID", get: (s) => s.store_id }];

// metric: "overall" or a leaderboard column index
const metricCol = (columns, metric, label, pick) => {
  if (metric === "overall") return { label, get: (s) => pick(s)?.overall?.rank ?? null };
  const c = columns[metric] || {};
  return c.format === "percent"
    ? { label, total: "avg", pct: true, get: (s) => (pick(s)?.lb?.[metric] == null ? null : Math.round(Number(pick(s).lb[metric]) * 1000) / 10) }
    : { label, total: "sum", get: (s) => (pick(s)?.lb?.[metric] == null ? null : Number(pick(s).lb[metric])) };
};

// One row per store with every month's row under s.byMonth[month], ordered by the latest month's overall rank
const sideRows = (d) => {
  const map = {};
  d.months.forEach(({ month, rows }) => rows.forEach((r) => { map[r.door_tsp] = { ...(map[r.door_tsp] || {}), ...r, byMonth: { ...(map[r.door_tsp]?.byMonth || {}), [month]: r } }; }));
  const last = d.months[d.months.length - 1]?.month;
  return Object.values(map).sort((a, b) => (a.byMonth[last]?.overall?.rank ?? 1e9) - (b.byMonth[last]?.overall?.rank ?? 1e9));
};

const monthCols = (d, metric) => d.months.map(({ month }) => metricCol(d.columns, metric, monthName(month), (s) => s.byMonth[month]));

export const leaderboardExport = (d, admin, byMonth, metric) => {
  if (byMonth && d.months) return { cols: [...storeCols(admin), ...monthCols(d, metric)], rows: sideRows(d) };
  const rows = [...(d.rows || [])].sort((a, b) => (a.overall?.rank ?? 1e9) - (b.overall?.rank ?? 1e9));
  return { rows, cols: [...storeCols(admin), metricCol(d.columns || [], "overall", "Overall Rank", (s) => s),
    ...(d.columns || []).map((c, i) => metricCol(d.columns, i, c.label, (s) => s))] };
};

const cell = (c, v) => (v == null ? "—" : c.pct ? fmtPct(v / 100) : fmtNum(v));

const SideBySide = ({ d, admin, metric, setMetric }) => {
  const rows = sideRows(d);
  const cols = monthCols(d, metric);
  return (
    <div className="space-y-3" data-testid="leaderboard-side-by-side">
      <select value={metric} onChange={(e) => setMetric(e.target.value === "overall" ? "overall" : Number(e.target.value))}
        className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="leaderboard-metric-select">
        <option value="overall">Overall Rank</option>
        {d.columns.map((c, i) => <option key={i} value={i}>{c.label}</option>)}
      </select>
      <table className="vz-compact w-full" data-testid="leaderboard-side-table">
        <thead><tr className="border-b border-slate-200 text-left text-slate-500">
          {admin && <StoreInfoHead className="text-left" />}
          <th>Store ID</th>
          {cols.map((c) => <th key={c.label} className="text-center">{c.label}</th>)}
        </tr></thead>
        <tbody>{rows.map((s) => (
          <tr key={s.door_tsp} className="border-b border-slate-100" data-testid={`leaderboard-side-row-${s.door_tsp}`}>
            {admin && <StoreInfoCells r={s} className="" />}
            <td className="font-medium text-navy">{s.store_id}</td>
            {cols.map((c) => <td key={c.label} className="text-center tabular-nums">{cell(c, c.get(s))}</td>)}
          </tr>
        ))}</tbody>
        {metric !== "overall" && <TotalsRow rows={rows} cols={cols} lead={admin ? 3 : 1} label={`Total (${rows.length})`} align="text-center" />}
      </table>
    </div>
  );
};

export const LeaderboardHistory = ({ d, admin, byMonth, metric, setMetric }) => (byMonth && d.months
  ? <SideBySide d={d} admin={admin} metric={metric} setMetric={setMetric} />
  : <StoreLeaderboard stores={d.rows || []} columns={d.columns || []} onSelect={() => {}} showStoreInfo={admin} embedded />);
